import { create } from 'zustand';

import MessagesService from 'src/services/MessagesService';

import type { IMessage, IMessagesRequest, IMessagesResponse, MessageLeadFeedback } from 'src/types/Message';

interface IMessagesStore {
    messages: IMessage[];
    count: number;
    addMessages: (messages: IMessage[]) => void;
    removeMessage: (id: number) => Promise<void>;
    toggleFavorite: (params: { id: number; flag: boolean }) => Promise<void>;
    setFeedback: (id: number, feedback: MessageLeadFeedback) => void;
    fetchMessages: (params?: IMessagesRequest) => Promise<IMessagesResponse>;
    deleteFromFavorite: (id: number) => void;
    clear: () => void;
}

export default create<IMessagesStore>((set, get) => ({
    messages: [],

    count: 0,

    addMessages: (messages: IMessage[]) =>
        set({
            messages: [...get().messages, ...messages],
        }),

    removeMessage: async (id) => {
        try {
            const { messages, count } = get();
            await MessagesService.removeMessage(id);

            const filteredMessages = messages.filter((message) => message.id !== id);
            // Обновляем счетчик, если он больше 0
            const newCount = count > 0 ? Math.max(0, count - 1) : count;

            set({ messages: filteredMessages, count: newCount });

            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        }
    },

    toggleFavorite: async ({ id, flag }) => {
        try {
            await MessagesService.toggleFavoriteMessage(id, flag);
            const { messages } = get();

            set({
                messages: messages.map((item) => {
                    if (item.id === id) {
                        return { ...item, favorite: flag };
                    }
                    return item;
                }),
            });

            return Promise.resolve();
        } catch (error) {
            return Promise.reject(error);
        }
    },

    setFeedback: (id, feedback) => {
        const { messages } = get();
        set({ messages: messages.map((item) => (item.id === id ? { ...item, feedback } : item)) });
    },

    fetchMessages: async (params = {} as IMessagesRequest) => {
        try {
            const { results, count, next } = await MessagesService.fetchMessages({
                ...params,
                limit: params.limit || 10,
            });

            // Если это первая страница (offset = 0 или undefined), заменяем данные
            // Иначе добавляем (для пагинации)
            const { messages } = get();
            const offset = params.offset || 0;

            if (offset === 0) {
                // Первая страница - заменяем все данные
                set({ messages: results, count });
            } else {
                // Последующие страницы - добавляем к существующим
                // Убираем дубликаты по ID
                const existingIds = new Set(messages.map((m) => m.id));
                const newMessages = results.filter((msg: IMessage) => !existingIds.has(msg.id));
                set({ messages: [...messages, ...newMessages], count });
            }

            return Promise.resolve({ results, count, next });
        } catch (error) {
            return Promise.reject(error);
        }
    },
    deleteFromFavorite: (id: number) => {
        const { messages } = get();

        set({ messages: messages.filter((message) => message.id !== id) });
    },
    clear: () => set({ messages: [], count: 0 }),
}));
